import React, { lazy } from 'react';
import { matchPath, RouteComponentProps, RouteProps } from 'react-router-dom';
import cloneDeep from 'lodash/cloneDeep';
import { useAppStore } from 'Store';
import {
    ALLOWED_ENTITIES,
    DENIED_ENTITIES,
    ALLOWED_ORGANIZATIONS,
    DENIED_ORGANIZATIONS
} from '@brighterion/utils/constants';
import { ROUTES } from './routes';

// Lazy-loaded route components (one chunk per page group)
const ConfigurationPage = lazy(() => import('Components/Wls/ConfigurationPage'));
const AllowedEntities = lazy(() => import('Components/Wls/ListPage/AllowedEntities/AllowedEntities'));
const DeniedEntities = lazy(() => import('Components/Wls/ListPage/DeniedEntities/DeniedEntities'));
const AllowedCountries = lazy(() => import('Components/Wls/ListPage/AllowedCountries/AllowedCountries'));
const DeniedCountries = lazy(() => import('Components/Wls/ListPage/DeniedCountries/DeniedCountries'));
const NoisyWords = lazy(() => import('Components/Wls/ListPage/NoisyWords'));
const EditWlsConfiguration = lazy(() => import('Components/EditConfiguration'));
const ListPage = lazy(() => import('Components/Wls/ListPage'));
const AdhocSearchPage = lazy(() => import('Components/Wls/AdhocSearch/AdhocSearchPage'));
const ListInnerPages = lazy(() => import('Components/Wls/ListPage/ListInnerPages'));
const AddressNoisyWords = lazy(() => import('Components/Wls/ListPage/AddressNoisyWords'));
const AllowedOrganizations = lazy(() => import('Components/Wls/ListPage/AllowedOrganizations/AllowedOrganizations'));
const DeniedOrganizations = lazy(() => import('Components/Wls/ListPage/DeniedOrganizations/DeniedOrganizations'));
const BatchScreening = lazy(() => import('Components/Wls/BatchScreening/index'));
const UploadFileBatchScreening = lazy(() => import('Components/Wls/BatchScreening/UploadFileBatchScreening'));
const TenantsPage = lazy(() => import('Components/Wls/Tenants'));

export const RoutesTree = (): RoutesTreeNode => {
    const entityPendingChanges = useAppStore?.use?.entityPendingChanges?.();
    const revisionHistoryById = useAppStore?.use?.revisionHistoryById?.();
    const revId = revisionHistoryById?.revId ? revisionHistoryById?.revId : '--';
    const entityReviewChanges = useAppStore?.use?.entityReviewChanges?.();
    const pendingRevisionId = entityPendingChanges?.revisionId ? entityPendingChanges?.revisionId : '--';
    const reviewRevisionId = entityReviewChanges?.revisionId ? entityReviewChanges?.revisionId : '--';
    return {
        path: '/',
        subNodes: [
            {
                path: ROUTES.SEARCH.INDEX,
                component: AdhocSearchPage,
                breadcrumbTitle: 'Search'
            },
            {
                path: ROUTES.BATCH_SCREENING.INDEX,
                component: BatchScreening,
                breadcrumbTitle: 'Batch Screening',
                subNodes: [
                    {
                        path: ROUTES.BATCH_SCREENING.UPLOAD_FILE_BATCH_SCREENING,
                        component: UploadFileBatchScreening
                    }
                ]
            },
            {
                path: ROUTES.TENANTS.INDEX,
                component: TenantsPage,
                breadcrumbTitle: 'Manage Tenants'
            },
            {
                path: ROUTES.LISTS.INDEX,
                component: ListPage,
                breadcrumbTitle: 'Internal Lists',
                subNodes: [
                    {
                        path: ROUTES.LISTS.ALLOWED_ENTITIES,
                        component: AllowedEntities,
                        breadcrumbTitle: ALLOWED_ENTITIES,
                        subNodes: [
                            {
                                path: ROUTES.LISTS.ALLOWED_ENTITIES + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_ENTITIES + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_ENTITIES + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.ALLOWED_ENTITIES +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.DENIED_ENTITIES,
                        component: DeniedEntities,
                        breadcrumbTitle: DENIED_ENTITIES,
                        subNodes: [
                            {
                                path: ROUTES.LISTS.DENIED_ENTITIES + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.DENIED_ENTITIES + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.DENIED_ENTITIES + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.DENIED_ENTITIES +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.ALLOWED_ORGANIZATIONS,
                        component: AllowedOrganizations,
                        breadcrumbTitle: ALLOWED_ORGANIZATIONS,
                        subNodes: [
                            {
                                path: ROUTES.LISTS.ALLOWED_ORGANIZATIONS + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_ORGANIZATIONS + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_ORGANIZATIONS + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.ALLOWED_ORGANIZATIONS +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.DENIED_ORGANIZATIONS,
                        component: DeniedOrganizations,
                        breadcrumbTitle: DENIED_ORGANIZATIONS,
                        subNodes: [
                            {
                                path: ROUTES.LISTS.DENIED_ORGANIZATIONS + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.DENIED_ORGANIZATIONS + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId}) `
                            },
                            {
                                path: ROUTES.LISTS.DENIED_ORGANIZATIONS + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.DENIED_ORGANIZATIONS +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.ALLOWED_COUNTRIES,
                        component: AllowedCountries,
                        breadcrumbTitle: 'Allowed Countries',
                        subNodes: [
                            {
                                path: ROUTES.LISTS.ALLOWED_COUNTRIES + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_COUNTRIES + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ALLOWED_COUNTRIES + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.ALLOWED_COUNTRIES +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.DENIED_COUNTRIES,
                        component: DeniedCountries,
                        breadcrumbTitle: 'Denied Countries',
                        subNodes: [
                            {
                                path: ROUTES.LISTS.DENIED_COUNTRIES + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.DENIED_COUNTRIES + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.DENIED_COUNTRIES + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.DENIED_COUNTRIES +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.NAME_NOISEWORDS,
                        component: NoisyWords,
                        breadcrumbTitle: 'Entity Noise Words',
                        subNodes: [
                            {
                                path: ROUTES.LISTS.NAME_NOISEWORDS + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.NAME_NOISEWORDS + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.NAME_NOISEWORDS + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.NAME_NOISEWORDS +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: ROUTES.LISTS.ADDRESS_NOISEWORDS,
                        component: AddressNoisyWords,
                        breadcrumbTitle: 'Address Noise Words',
                        subNodes: [
                            {
                                path: ROUTES.LISTS.ADDRESS_NOISEWORDS + ROUTES.LISTS.PENDING_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Pending Changes (Revision ID ${pendingRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ADDRESS_NOISEWORDS + ROUTES.LISTS.REVIEW_CHANGES,
                                component: ListInnerPages,
                                breadcrumbTitle: `Review Changes (Revision ID ${reviewRevisionId})`
                            },
                            {
                                path: ROUTES.LISTS.ADDRESS_NOISEWORDS + ROUTES.LISTS.REVISION_HISTORY,
                                component: ListInnerPages,
                                breadcrumbTitle: 'Revision History',
                                subNodes: [
                                    {
                                        path:
                                            ROUTES.LISTS.ADDRESS_NOISEWORDS +
                                            ROUTES.LISTS.REVISION_HISTORY +
                                            ROUTES.LISTS.REVISION_HISTORY_BY_ID,
                                        component: ListInnerPages,
                                        breadcrumbTitle: `Revision ID ${revId}`
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                path: ROUTES.CONFIGURATION.INDEX,
                component: ConfigurationPage,
                breadcrumbTitle: 'Configuration',
                subNodes: [
                    {
                        path: ROUTES.CONFIGURATION.EDIT,
                        component: EditWlsConfiguration,
                        breadcrumbTitle: 'Edit Configuration'
                    }
                ]
            }
        ]
    };
};

export const getBreadcrumbs = (
    location: RouteComponentProps['location'],
    iterationCount = 1,
    node: RoutesTreeNode = RoutesTree(),
    accumulator: BreadcrumbWrapper = { breadcrumbs: [] }
): BreadcrumbWrapper | null => {
    const { pathname } = location;
    const pathnameLength = pathname.split('/')?.length;
    const nodePathLength = node.path.split('/')?.length;
    // truncate pathname to the parts relevant to the current node.path value
    const truncatedPathname = pathname.split('/').splice(0, nodePathLength).join('/');

    // flag that allows the search to end if the depth of the tree is the same as the number of elements
    // in the current path name's split array
    const isLastPathnameIteration = pathnameLength === iterationCount;
    const pathMatched = matchPath(pathname, {
        path: node.path,
        exact: isLastPathnameIteration
    });

    if (!pathMatched) {
        return null;
    }

    const copiedAccumulator: BreadcrumbWrapper = {
        ...accumulator
    };

    const { params } = pathMatched;

    if (node.breadcrumbTitle) {
        copiedAccumulator.breadcrumbs.push({
            title: node.breadcrumbTitle,
            hasAdditionalParams: !!node.hasAdditionalParams,
            providedPath: node.path,
            actualPath: truncatedPathname,
            params: { ...params, ...node?.params }
        });
    }

    if (!isLastPathnameIteration) {
        if (node.subNodes) {
            // eslint-disable-next-line no-restricted-syntax
            for (const subNode of node.subNodes) {
                const subResults = getBreadcrumbs(location, iterationCount + 1, subNode, copiedAccumulator);
                if (subResults) {
                    return subResults;
                }
            }
        }
    }

    return copiedAccumulator;
};

const recursiveFlatRouteHelper = (
    acc: Pick<RouteProps, 'path' | 'component'>[],
    node: RoutesTreeNode
): Pick<RouteProps, 'path' | 'component'>[] => {
    const clonedNode = cloneDeep(node);

    if (node.component) {
        acc.push({
            path: clonedNode.path,
            component: clonedNode.component as React.FC
        });
    }

    if (!clonedNode.subNodes || clonedNode?.subNodes?.length === 0) return acc;

    return clonedNode.subNodes.reduce(
        (thisAcc: Pick<RouteProps, 'path' | 'component'>[], thisNode: RoutesTreeNode) =>
            recursiveFlatRouteHelper(thisAcc, thisNode),
        acc
    );
};

export const getFlatRoutesArray = (): Pick<RouteProps, 'path' | 'component'>[] => {
    const node: RoutesTreeNode = RoutesTree();
    return recursiveFlatRouteHelper([], node);
};
